import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db, functions } from '@/config/firebase';
import { AuditLog } from '@/types';
import { withErrorHandling } from '@/utils/errorHandling';

/**
 * Audit Log Service
 * HIPAA Compliance: All access to PHI/PII must be logged
 *
 * This service provides functions to log user actions for audit purposes.
 * All logs are written via Cloud Functions to ensure security and immutability.
 */

// Action types for audit logging
export type AuditAction =
  // Client actions
  | 'view_client'
  | 'create_client'
  | 'edit_client'
  | 'delete_client'
  | 'download_hipaa'
  | 'upload_hipaa'
  | 'upload_hipaa_auth'
  | 'replace_hipaa_auth'
  | 'confirm_intake_identity_edit'
  | 'delete_hipaa_auth'
  | 'close_case'
  | 'reopen_case'
  | 'upload_signature'
  // Provider actions
  | 'view_provider'
  | 'create_provider'
  | 'edit_provider'
  | 'delete_provider'
  // Facility actions
  | 'create_facility'
  | 'update_facility'
  | 'add_affiliation'
  | 'remove_affiliation'
  | 'set_primary_affiliation'
  // Client-Provider actions
  | 'add_client_provider'
  | 'edit_client_provider'
  | 'delete_client_provider'
  | 'toggle_auto_request'
  | 'stop_requests'
  | 'resume_recurring_requests'
  // Request actions
  | 'view_request'
  | 'create_request'
  | 'approve_request'
  | 'deny_request'
  | 'send_request'
  | 'mark_response_received'
  | 'update_request_status'
  | 'snooze_request'
  | 'unsnooze_request'
  | 'resume_auto_reminders'
  | 'schedule_followup'
  // Follow-up actions
  | 'create_followup'
  | 'edit_followup'
  | 'delete_followup'
  | 'view_followup'
  // Document actions
  | 'upload_document'
  | 'view_document'
  | 'download_document'
  | 'print_document'
  | 'edit_document'
  | 'delete_document'
  | 'replace_document'
  | 'create_generated_document'
  // User management actions
  | 'create_user'
  | 'edit_user'
  | 'delete_user'
  | 'deactivate_user'
  | 'activate_user'
  | 'send_invitation'
  | 'resend_invitation'
  | 'cancel_invitation'
  // Auth actions
  | 'login'
  | 'logout'
  | 'login_failed'
  | 'password_reset_requested'
  | 'password_reset_completed'
  // Settings actions
  | 'update_firm_settings'
  | 'update_email_template'
  | 'update_subscription';

// Resource types for audit logging
export type ResourceType =
  | 'client'
  | 'provider'
  | 'facility'
  | 'clientProvider'
  | 'request'
  | 'document'
  | 'user'
  | 'settings'
  | 'subscription'
  | 'billing'
  | 'auditLog';

// Audit log entry interface
export interface AuditLogEntry {
  firmId: string;
  action: AuditAction;
  resourceType: ResourceType;
  resourceId?: string;
  details?: Record<string, unknown>;
  success?: boolean;
  errorMessage?: string;
}

/**
 * Get Cloud Functions callables (lazy initialization)
 */
function getAuditFunctions() {
  return {
    createAuditLog: httpsCallable<AuditLogEntry, { success: boolean; auditLogId: string }>(
      functions,
      'createAuditLog'
    ),
    batchCreateAuditLogs: httpsCallable<
      { firmId: string; logs: Omit<AuditLogEntry, 'firmId'>[] },
      { success: boolean; count: number; auditLogIds: string[] }
    >(functions, 'batchCreateAuditLogs'),
  };
}

/**
 * Create a single audit log entry
 *
 * @param entry - The audit log entry to create
 * @returns Promise<void>
 */
export async function logAudit(entry: AuditLogEntry, options: { rethrow?: boolean } = {}): Promise<void> {
  await withErrorHandling(async () => {
    const { createAuditLog } = getAuditFunctions();
    await createAuditLog(entry);
  }, 'creating audit log', { silent: true, rethrow: options.rethrow ?? false });
}

/**
 * Create multiple audit log entries in a batch
 * More efficient than calling logAudit multiple times
 */
export async function logAuditBatch(
  firmId: string,
  logs: Omit<AuditLogEntry, 'firmId'>[]
): Promise<void> {
  await withErrorHandling(async () => {
    const { batchCreateAuditLogs } = getAuditFunctions();
    await batchCreateAuditLogs({ firmId, logs });
  }, 'batch creating audit logs', { silent: true, rethrow: false });
}

/**
 * Helper function to log client view with details about what was accessed
 */
export async function logClientView(
  firmId: string,
  clientId: string,
  viewedSensitiveData: boolean = false
): Promise<void> {
  await logAudit({
    firmId,
    action: 'view_client',
    resourceType: 'client',
    resourceId: clientId,
    details: {
      viewedSensitiveData,
    },
    success: true,
  });
}

/**
 * Log a protected HIPAA PDF opened from the client's authorization section
 * (#1484). IDs only: no client name, and no URL, since a URL can be a credential.
 */
export async function logHipaaPdfView(
  firmId: string,
  clientId: string,
  details: {
    view: 'reusable_pages' | 'signed_upload' | 'current_upload';
    documentId?: string;
    versionId?: string;
  }
): Promise<void> {
  await logAudit({
    firmId,
    action: 'download_hipaa',
    resourceType: 'client',
    resourceId: clientId,
    details,
    success: true,
  });
}

/**
 * Log a document sent to the printer from the document viewer (#1484).
 * IDs only, like View/Download (#1498); the client name is resolved at display time.
 */
export async function logDocumentPrint(firmId: string, documentId: string, clientId?: string): Promise<void> {
  await logAudit({
    firmId,
    action: 'print_document',
    resourceType: 'document',
    resourceId: documentId,
    ...(clientId ? { details: { clientId } } : {}),
    success: true,
  });
}

/**
 * Helper function to log user management actions
 */
export async function logUserManagement(
  firmId: string,
  action: Extract<AuditAction, 'create_user' | 'edit_user' | 'delete_user' | 'deactivate_user' | 'activate_user'>,
  userId: string,
  userEmail: string,
  details?: Record<string, unknown>
): Promise<void> {
  await logAudit({
    firmId,
    action,
    resourceType: 'user',
    resourceId: userId,
    details: {
      userEmail,
      ...details,
    },
    success: true,
  });
}

/**
 * Helper function to log settings changes
 */
export async function logSettingsChange(
  firmId: string,
  action: Extract<AuditAction, 'update_firm_settings' | 'update_email_template' | 'update_subscription'>,
  changesMade: Record<string, unknown>
): Promise<void> {
  await logAudit({
    firmId,
    action,
    resourceType: 'settings',
    details: {
      changesMade,
    },
    success: true,
  });
}

/**
 * Helper function to log failed login attempts
 * Uses a special unauthenticated endpoint since user hasn't logged in yet
 */
export async function logLoginAttempt(
  email: string,
  success: boolean,
  errorMessage?: string
): Promise<void> {
  // Only log failed attempts (successful logins use logSuccessfulLogin)
  if (success) return;

  await withErrorHandling(async () => {
    const logFailedLogin = httpsCallable<
      { email: string; errorMessage?: string },
      { success: boolean; auditLogId: string }
    >(functions, 'logFailedLoginAttempt');

    await logFailedLogin({ email, errorMessage });
  }, 'logging failed login attempt', { silent: true, rethrow: false });
}

/**
 * Helper function to log successful login
 */
export async function logSuccessfulLogin(firmId: string, userId: string, userEmail: string): Promise<void> {
  await logAudit({
    firmId,
    action: 'login',
    resourceType: 'user',
    resourceId: userId,
    details: {
      userEmail,
      timestamp: new Date().toISOString(),
    },
    success: true,
  });
}

/**
 * Helper function to log logout
 */
export async function logLogout(firmId: string, userId: string, userEmail: string): Promise<void> {
  await logAudit({
    firmId,
    action: 'logout',
    resourceType: 'user',
    resourceId: userId,
    details: {
      userEmail,
      timestamp: new Date().toISOString(),
    },
    success: true,
  });
}

/**
 * Helper function to log request approval/denial
 */
export async function logRequestApproval(
  firmId: string,
  requestId: string,
  approved: boolean,
  target: { clientId: string; providerId?: string }
): Promise<void> {
  await logAudit({
    firmId,
    action: approved ? 'approve_request' : 'deny_request',
    resourceType: 'request',
    resourceId: requestId,
    details: {
      ...target,
      decision: approved ? 'approved' : 'denied',
    },
    success: true,
  });
}

/**
 * Get recent audit logs for a firm
 */
export async function getAuditLogsByFirm(
  firmId: string,
  limitCount: number = 50
): Promise<AuditLog[]> {
  return (await withErrorHandling(async () => {
    const auditLogsRef = collection(db, `firms/${firmId}/auditLogs`);
    const q = query(auditLogsRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      auditLogId: doc.id,
      ...doc.data()
    })) as unknown as AuditLog[];
  }, 'fetching audit logs', { rethrow: false })) || [];
}
